import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import { array, bool, func, object, string } from "prop-types";
import Helmet from "react-helmet";
import * as prismic from "@prismicio/client";
import throttle from "lodash/throttle";

import { routes } from "../../constants";
import { formatTitleCase } from "../../helpers";
import { usePrismicClient } from "@prismicio/react";

import { Redirect } from "react-router-dom";
import Meta from "../meta";

const defaultTitle = "HOLT Used Blog";

const getBlogIndexComponent = targetDevice =>
  !!targetDevice
    ? lazy(() => import(`./components/BlogIndex.${targetDevice}`))
    : null;

const BlogIndex = ({
  blogCategories,
  blogCategoriesError,
  blogCategoriesLoading,
  fetchBlogCategories,
  match,
  prerenderReady,
  targetDevice,
}) => {
  const client = usePrismicClient();
  const [blogPosts, setBlogPosts] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [request, setRequest] = useState();
  const [redirect, setRedirect] = useState(false);

  const isFetching = useRef(false);
  const isMounted = useRef(true);
  const fetchedAll = useRef(false);

  const BlogIndexComponent = useMemo(
    () => getBlogIndexComponent(targetDevice),
    [targetDevice]
  );

  // Method for fetching blog posts from Prismic
  const fetchPosts = useCallback(() => {
    if (!request || (!!request && !request.value) || fetchedAll.current) {
      return;
    }

    isFetching.current = true;

    client
      .get({
        filters: [prismic.filter.at(request.param, request.value)],
        pageSize: 10,
        page: request.page,
        orderings: [{ field: "my.blog_post.date_posted", direction: "desc" }],
      })
      .then(response => {
        isFetching.current = false;

        if (!!response && !!response.results) {
          if (response.page >= response.total_pages) {
            fetchedAll.current = true;
          } else {
            setRequest({ ...request, page: response.page + 1 });
          }

          if (response.page === 1 && request.type === "all") {
            const feat = response.results[0];
            setFeatured(!!feat ? { ...feat.data, uid: feat.uid } : null);
            setBlogPosts(posts => [...posts, ...response.results.slice(1)]);
          } else {
            setBlogPosts(posts => [...posts, ...response.results]);
          }

          // Ready for prerender after first page of results loads
          if (!!prerenderReady && response.page === 1) {
            prerenderReady();
          }
        } else {
          console.warn(response);
        }
      })
      .catch(error => {
        console.warn(error);

        isFetching.current = false;
      });
  }, [client, request, prerenderReady]);

  // Fetch blog categories
  useEffect(() => {
    if (
      !blogCategoriesLoading &&
      !blogCategoriesError &&
      !blogCategories.length
    ) {
      fetchBlogCategories();
    }
  }, [
    blogCategories,
    blogCategoriesError,
    blogCategoriesLoading,
    fetchBlogCategories,
  ]);

  // Method for handling infinite scroll
  const handleScroll = throttle(e => {
    if (!isMounted.current || isFetching.current) {
      return;
    }

    const pageWrapper = document.getElementById("page-wrapper");
    const bottomPos = !!pageWrapper
      ? pageWrapper.scrollHeight
      : document.body.offsetHeight;
    const scrollPos = !!pageWrapper
      ? pageWrapper.offsetHeight + pageWrapper.scrollTop
      : window.innerHeight + window.pageYOffset;

    if (scrollPos >= bottomPos - 300) {
      fetchPosts();
    }
  }, 500);

  // Attach scroll listeners
  useEffect(() => {
    window.addEventListener("resize", handleScroll);
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("touchmove", handleScroll);
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleScroll);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("touchmove", handleScroll);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [handleScroll, fetchPosts]);

  // Define base Prismic request
  useEffect(() => {
    setFeatured(null);
    setRedirect(false);
    fetchedAll.current = false;

    const isCategory = match.url.includes(routes.blogCategory.path);
    const isTag = match.url.includes(routes.blogTag.path);
    const name =
      match.params.name &&
      match.params.name.trim().replace(/\s+/g, "-").toLowerCase();

    // Request posts w/ tag
    if (!!name && isTag) {
      const tagNice = formatTitleCase(name.replace("-", " "));

      setRequest({
        type: "tag",
        name: name,
        title: tagNice,
        param: "document.tags",
        value: [tagNice],
        page: 1,
      });
    }

    // Request posts w/ category
    else if (!!name && isCategory) {
      setRequest({
        type: "category",
        name: name,
        param: "my.blog_post.categories.cat",
        page: 1,
      });
    }

    // Request all posts
    else if (!name && !isCategory && !isTag) {
      setRequest({
        type: "all",
        name: "",
        param: "document.type",
        value: "blog_post",
        page: 1,
      });
    }

    // Otherwise redirect main page
    else {
      setRedirect(true);
    }
  }, [match]);

  // Set category ID as value for category requests
  useEffect(() => {
    if (
      !!blogCategories.length &&
      !!request &&
      request.type === "category" &&
      !request.value
    ) {
      const currentCategory = blogCategories.find(
        ({ uid }) => uid === request.name
      );

      if (!!currentCategory) {
        setRequest({
          ...request,
          title: currentCategory.data.name,
          value: currentCategory.id,
        });
      } else {
        isFetching.current = false;
        fetchedAll.current = true;

        setRequest({
          ...request,
          title: request.name,
          value: "notfound",
        });
      }
    }
  }, [blogCategories, request]);

  // Send first request for posts once the inital request definition is complete
  useEffect(() => {
    if (!!request && !!request.value && request.page === 1) {
      setBlogPosts([]);
      fetchPosts();
    }
  }, [fetchPosts, request]);

  return redirect ? (
    <Redirect to={routes.blog.path} />
  ) : (
    <>
      <Meta
        title={(!!request && request.title) || defaultTitle}
        description="An index of articles on holt used for knowledge and product information"
        noIndex={!!request && request.type !== "all"}
      />
      <Helmet defer={false}>
        <meta
          property="og:title"
          content={(!!request && request.title) || defaultTitle}
        />
        <meta
          property="og:description"
          content="An index of articles on holt used for knowledge and product information"
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <Suspense fallback={null}>
        {!!BlogIndexComponent && (
          <BlogIndexComponent
            blogCategories={blogCategories}
            blogPosts={blogPosts}
            featured={featured}
            fetchedAll={fetchedAll.current}
            isFetching={isFetching.current}
            title={request && request.title}
            type={request && request.type}
          />
        )}
      </Suspense>
    </>
  );
};

BlogIndex.propTypes = {
  blogCategories: array,
  blogCategoriesError: string,
  blogCategoriesLoading: bool,
  fetchBlogCategories: func.isRequired,
  match: object.isRequired,
  prerenderReady: func.isRequired,
  targetDevice: string,
};

export default BlogIndex;
